import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Template from "@/services/template/template";
import type { GetTemplate } from "@/dtos/Template.dto";

export interface TemplatesParams {
  organizationId: string;
  id?: string;
  templateFolderId?: string;
  startDate?: string;
  endDate?: string;
  createdById?: string | null;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useTemplates(params: Ref<TemplatesParams | null> | TemplatesParams | null) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["templates", "disabled"];
    return [
      "templates",
      p.organizationId,
      p.id || "",
      p.templateFolderId || "",
      p.startDate || "",
      p.endDate || "",
      p.createdById ?? "all",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetTemplate = {
        organizationId: p.organizationId,
      };

      if (p.id) queryParams.id = p.id;
      if (p.templateFolderId) queryParams.templateFolderId = p.templateFolderId;
      if (p.startDate) queryParams.startDate = p.startDate;
      if (p.endDate) queryParams.endDate = p.endDate;
      if (p.createdById !== undefined) queryParams.createdById = p.createdById;

      const result = await Template.getTemplates(queryParams);

      if (result.data.status) {
        return {
          data: result.data.data,
          metaData: result.data.metaData || {},
        };
      }

      throw new Error(result.data.msg || "Failed to load templates");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
